/**
 * Visualizer - Beautiful audio visualizer component
 * Synthwave/Retrowave aesthetic with neon glow effects
 *
 * Features:
 * - Oscilloscope with gradient and glow
 * - Spectrum analyzer with neon bars
 * - Reflection effects
 * - Completely isolated, no persistence
 */

import * as Tone from 'tone';

export class Visualizer {
    constructor(audioEngine) {
        this.audioEngine = audioEngine;
        this.waveformAnalyser = null;
        this.fftAnalyser = null;
        this.canvas = null;
        this.ctx = null;
        this.animationId = null;
        this.isActive = false;
        this.mode = 'both'; // 'oscilloscope', 'spectrum', 'both'

        // Smoothed data for prettier animations
        this.smoothedWaveform = null;
        this.smoothedFFT = null;

        // Visual settings
        this.colors = {
            cyan: '#00ffff',
            magenta: '#ff00ff',
            purple: '#8800ff',
            pink: '#ff0088',
            orange: '#ff8800',
            background: '#0a0a15',
            backgroundGradient: ['#0a0a15', '#1a0a25', '#0a1a25']
        };

        this.container = null;
    }

    /**
     * Create the visualizer UI
     */
    createUI(parentSelector = '#visualizerContainer') {
        const parent = document.querySelector(parentSelector);
        if (!parent) return;

        this.container = document.createElement('div');
        this.container.className = 'visualizer-panel';
        this.container.innerHTML = `
            <div class="visualizer-header">
                <span class="visualizer-title">✨ Audio Visualizer</span>
                <div class="visualizer-controls">
                    <button class="viz-mode-btn active" data-mode="both">Both</button>
                    <button class="viz-mode-btn" data-mode="oscilloscope">Scope</button>
                    <button class="viz-mode-btn" data-mode="spectrum">Spectrum</button>
                    <button class="viz-mode-btn" data-mode="spectrograph">Spectrograph</button>
                </div>
            </div>
            <div class="visualizer-canvas-container">
                <canvas class="visualizer-canvas"></canvas>
                <div class="visualizer-reflection"></div>
            </div>
        `;

        // Spectrograph history buffer (for waterfall effect)
        this.spectrographHistory = [];

        parent.appendChild(this.container);
        this.container.style.display = 'none';

        // Get canvas
        this.canvas = this.container.querySelector('.visualizer-canvas');
        this.ctx = this.canvas.getContext('2d');

        // Setup canvas size
        this.resizeCanvas();
        window.addEventListener('resize', () => this.resizeCanvas());

        // Setup controls
        this.setupControls();
    }

    /**
     * Setup control event listeners
     */
    setupControls() {
        // Mode buttons
        this.container.querySelectorAll('.viz-mode-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                this.container.querySelectorAll('.viz-mode-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.mode = btn.dataset.mode;
                // Clear spectrograph history when switching modes
                this.spectrographHistory = [];
            });
        });
    }

    /**
     * Resize canvas to container
     */
    resizeCanvas() {
        if (!this.canvas) return;
        const container = this.canvas.parentElement;
        const rect = container.getBoundingClientRect();
        this.canvas.width = rect.width || 800;
        this.canvas.height = rect.height || 200;
    }

    /**
     * Activate the visualizer
     */
    activate() {
        if (this.isActive) return;

        // Create Tone.js analysers
        this.waveformAnalyser = new Tone.Analyser('waveform', 1024);
        this.fftAnalyser = new Tone.Analyser('fft', 256);
        this.fftAnalyser.smoothing = 0.8;

        // Connect to master output
        const masterVolume = this.audioEngine.getMasterVolume();
        masterVolume.connect(this.waveformAnalyser);
        masterVolume.connect(this.fftAnalyser);

        // Initialize smoothed data arrays
        this.smoothedFFT = new Float32Array(256);

        // Show container
        this.container.style.display = 'block';
        this.resizeCanvas();

        // Start animation
        this.isActive = true;
        this.animate();
    }

    /**
     * Deactivate the visualizer
     */
    deactivate() {
        if (!this.isActive) return;

        // Stop animation
        this.isActive = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        // Disconnect and dispose analysers
        if (this.waveformAnalyser) {
            this.waveformAnalyser.dispose();
            this.waveformAnalyser = null;
        }
        if (this.fftAnalyser) {
            this.fftAnalyser.dispose();
            this.fftAnalyser = null;
        }

        // Hide container
        this.container.style.display = 'none';
    }

    /**
     * Toggle visualizer
     */
    toggle() {
        if (this.isActive) {
            this.deactivate();
        } else {
            this.activate();
        }
        return this.isActive;
    }

    /**
     * Main animation loop
     */
    animate() {
        if (!this.isActive) return;

        // Get audio data from Tone.js analysers
        this.waveformData = this.waveformAnalyser.getValue();
        this.fftData = this.fftAnalyser.getValue();

        // Normalize FFT data from dB to 0-1 range
        const minDb = -70;  // Floor (silence)
        const maxDb = -10;  // Ceiling (loud)
        const dbRange = maxDb - minDb;

        for (let i = 0; i < this.fftData.length; i++) {
            // Clamp dB value to range and normalize to 0-1
            const db = this.fftData[i];
            const normalized = Math.max(0, Math.min(1, (db - minDb) / dbRange));
            // Smooth for prettier animations
            this.smoothedFFT[i] = this.smoothedFFT[i] * 0.8 + normalized * 0.2;
        }

        // Clear and draw background
        this.drawBackground();

        // Draw based on mode
        if (this.mode === 'oscilloscope' || this.mode === 'both') {
            this.drawOscilloscope();
        }
        if (this.mode === 'spectrum' || this.mode === 'both') {
            this.drawSpectrum();
        }
        if (this.mode === 'spectrograph') {
            this.drawSpectrograph();
        }

        // Draw overlay effects
        this.drawOverlay();

        // Continue animation
        this.animationId = requestAnimationFrame(() => this.animate());
    }

    /**
     * Draw gradient background
     */
    drawBackground() {
        const { width, height } = this.canvas;
        const gradient = this.ctx.createLinearGradient(0, 0, 0, height);
        gradient.addColorStop(0, '#0a0a18');
        gradient.addColorStop(0.5, '#12082a');
        gradient.addColorStop(1, '#0a1520');

        this.ctx.fillStyle = gradient;
        this.ctx.fillRect(0, 0, width, height);

        // Add subtle grid
        this.drawGrid();
    }

    /**
     * Draw subtle background grid
     */
    drawGrid() {
        const { width, height } = this.canvas;
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
        this.ctx.lineWidth = 1;

        // Vertical lines
        const vSpacing = 40;
        for (let x = 0; x < width; x += vSpacing) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, 0);
            this.ctx.lineTo(x, height);
            this.ctx.stroke();
        }

        // Horizontal lines
        const hSpacing = 20;
        for (let y = 0; y < height; y += hSpacing) {
            this.ctx.beginPath();
            this.ctx.moveTo(0, y);
            this.ctx.lineTo(width, y);
            this.ctx.stroke();
        }

        // Center line (brighter)
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
        this.ctx.beginPath();
        this.ctx.moveTo(0, height / 2);
        this.ctx.lineTo(width, height / 2);
        this.ctx.stroke();
    }

    /**
     * Draw oscilloscope waveform
     */
    drawOscilloscope() {
        const { width, height } = this.canvas;
        const bufferLength = this.waveformData.length;
        const sliceWidth = width / bufferLength;
        const centerY = this.mode === 'both' ? height * 0.35 : height / 2;
        const amplitude = this.mode === 'both' ? height * 0.25 : height * 0.4;

        // Create gradient for the line
        const gradient = this.ctx.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, this.colors.cyan);
        gradient.addColorStop(0.3, this.colors.magenta);
        gradient.addColorStop(0.6, this.colors.pink);
        gradient.addColorStop(1, this.colors.cyan);

        // Draw glow layer (larger, more transparent)
        this.ctx.save();
        this.ctx.shadowColor = this.colors.cyan;
        this.ctx.shadowBlur = 20;
        this.ctx.strokeStyle = gradient;
        this.ctx.lineWidth = 3;
        this.ctx.lineCap = 'round';
        this.ctx.lineJoin = 'round';

        this.ctx.beginPath();
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
            // Tone.js waveform data is -1 to 1
            const v = this.waveformData[i];
            const y = centerY - v * amplitude;

            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
            x += sliceWidth;
        }
        this.ctx.stroke();

        // Draw main line (sharper)
        this.ctx.shadowBlur = 5;
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
        this.ctx.restore();

        // Draw reflection (if in 'both' mode, skip to not overlap with spectrum)
        if (this.mode === 'oscilloscope') {
            this.drawOscilloscopeReflection(centerY, amplitude, sliceWidth, bufferLength);
        }
    }

    /**
     * Draw oscilloscope reflection
     */
    drawOscilloscopeReflection(centerY, amplitude, sliceWidth, bufferLength) {
        const { width, height } = this.canvas;
        const reflectionY = height - centerY;

        const gradient = this.ctx.createLinearGradient(0, 0, width, 0);
        gradient.addColorStop(0, 'rgba(0, 255, 255, 0.2)');
        gradient.addColorStop(0.5, 'rgba(255, 0, 255, 0.2)');
        gradient.addColorStop(1, 'rgba(0, 255, 255, 0.2)');

        this.ctx.strokeStyle = gradient;
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.3;

        this.ctx.beginPath();
        let x = 0;
        for (let i = 0; i < bufferLength; i++) {
            const v = this.waveformData[i];
            const y = reflectionY + v * amplitude * 0.5;

            if (i === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
            x += sliceWidth;
        }
        this.ctx.stroke();
        this.ctx.globalAlpha = 1;
    }

    /**
     * Get logarithmically scaled frequency bin index
     */
    getLogFrequencyIndex(barIndex, barCount, binCount) {
        // Logarithmic scale: more resolution in low frequencies, less in high
        const minFreq = 20;
        const maxFreq = 16000;
        const logMin = Math.log10(minFreq);
        const logMax = Math.log10(maxFreq);

        const logFreq = logMin + (barIndex / barCount) * (logMax - logMin);
        const freq = Math.pow(10, logFreq);

        // Convert frequency to bin index (assuming 44100 sample rate, 512 FFT size)
        const nyquist = 22050;
        const binIndex = Math.floor((freq / nyquist) * binCount);

        return Math.min(binIndex, binCount - 1);
    }

    /**
     * Draw spectrum analyzer
     */
    drawSpectrum() {
        const { width, height } = this.canvas;
        const barCount = 64;
        const barWidth = (width / barCount) * 0.7;
        const barGap = (width / barCount) * 0.3;
        const startY = this.mode === 'both' ? height * 0.55 : height * 0.1;
        const maxBarHeight = this.mode === 'both' ? height * 0.4 : height * 0.8;

        for (let i = 0; i < barCount; i++) {
            // Use logarithmic frequency scaling
            const dataIndex = this.getLogFrequencyIndex(i, barCount, this.smoothedFFT.length);
            const value = this.smoothedFFT[dataIndex];
            const barHeight = Math.max(2, value * maxBarHeight);

            const x = i * (barWidth + barGap) + barGap / 2;
            const y = startY + maxBarHeight - barHeight;

            // Create gradient for each bar
            const gradient = this.ctx.createLinearGradient(0, y + barHeight, 0, y);

            // Color based on frequency (low = warm, high = cool)
            const hue = 280 - (i / barCount) * 120; // Purple to cyan
            gradient.addColorStop(0, `hsla(${hue}, 100%, 50%, 0.8)`);
            gradient.addColorStop(0.5, `hsla(${hue + 30}, 100%, 60%, 0.9)`);
            gradient.addColorStop(1, `hsla(${hue + 60}, 100%, 70%, 1)`);

            // Draw glow
            this.ctx.save();
            this.ctx.shadowColor = `hsl(${hue}, 100%, 50%)`;
            this.ctx.shadowBlur = 15;
            this.ctx.fillStyle = gradient;

            // Rounded rectangle
            this.roundRect(x, y, barWidth, barHeight, 2);
            this.ctx.fill();
            this.ctx.restore();

            // Draw reflection
            this.drawBarReflection(x, startY + maxBarHeight, barWidth, barHeight, hue);
        }
    }

    /**
     * Draw bar reflection
     */
    drawBarReflection(x, startY, barWidth, barHeight, hue) {
        const reflectionHeight = barHeight * 0.4;
        const gradient = this.ctx.createLinearGradient(0, startY, 0, startY + reflectionHeight);
        gradient.addColorStop(0, `hsla(${hue}, 100%, 50%, 0.3)`);
        gradient.addColorStop(1, `hsla(${hue}, 100%, 50%, 0)`);

        this.ctx.fillStyle = gradient;
        this.roundRect(x, startY + 2, barWidth, reflectionHeight, 2);
        this.ctx.fill();
    }

    /**
     * Draw spectrograph (waterfall display - horizontal scroll, frequency on Y axis)
     */
    drawSpectrograph() {
        const { width, height } = this.canvas;
        const freqBins = 128;

        // Create current frame data with logarithmic scaling
        const currentFrame = [];
        for (let i = 0; i < freqBins; i++) {
            const dataIndex = this.getLogFrequencyIndex(i, freqBins, this.smoothedFFT.length);
            currentFrame.push(this.smoothedFFT[dataIndex]);
        }

        // Add to history (newest on right)
        this.spectrographHistory.push(currentFrame);

        // Limit history length to canvas width
        const columnWidth = 2;
        const maxHistory = Math.floor(width / columnWidth);
        while (this.spectrographHistory.length > maxHistory) {
            this.spectrographHistory.shift();
        }

        // Draw the spectrograph (time on X, frequency on Y)
        const binHeight = height / freqBins;

        for (let col = 0; col < this.spectrographHistory.length; col++) {
            const frameData = this.spectrographHistory[col];
            const x = col * columnWidth;

            for (let i = 0; i < freqBins; i++) {
                const value = frameData[i];
                // Invert Y so low frequencies are at bottom
                const y = height - (i + 1) * binHeight;

                // Color based on intensity (dark purple -> magenta -> cyan -> white)
                let hue, saturation, lightness;
                if (value < 0.3) {
                    hue = 280; // Purple
                    saturation = 80;
                    lightness = 10 + value * 40;
                } else if (value < 0.6) {
                    hue = 300 - (value - 0.3) * 100; // Magenta to pink
                    saturation = 100;
                    lightness = 30 + value * 30;
                } else {
                    hue = 180 + (1 - value) * 40; // Cyan
                    saturation = 100;
                    lightness = 50 + value * 30;
                }

                const alpha = 0.2 + value * 0.8;
                this.ctx.fillStyle = `hsla(${hue}, ${saturation}%, ${lightness}%, ${alpha})`;
                this.ctx.fillRect(x, y, columnWidth, binHeight + 0.5);
            }
        }

        // Draw frequency labels on left side
        this.ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
        this.ctx.font = '9px monospace';
        this.ctx.fillText('16k', 3, 12);
        this.ctx.fillText('1k', 3, height * 0.5);
        this.ctx.fillText('20', 3, height - 3);

        // Draw subtle grid lines
        this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        this.ctx.lineWidth = 1;
        this.ctx.setLineDash([2, 4]);

        // Horizontal lines at key frequencies
        [0.25, 0.5, 0.75].forEach(pos => {
            this.ctx.beginPath();
            this.ctx.moveTo(20, height * pos);
            this.ctx.lineTo(width, height * pos);
            this.ctx.stroke();
        });

        this.ctx.setLineDash([]);
    }

    /**
     * Draw rounded rectangle
     */
    roundRect(x, y, width, height, radius) {
        if (height < 0) return;
        this.ctx.beginPath();
        this.ctx.moveTo(x + radius, y);
        this.ctx.lineTo(x + width - radius, y);
        this.ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        this.ctx.lineTo(x + width, y + height - radius);
        this.ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        this.ctx.lineTo(x + radius, y + height);
        this.ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        this.ctx.lineTo(x, y + radius);
        this.ctx.quadraticCurveTo(x, y, x + radius, y);
        this.ctx.closePath();
    }

    /**
     * Draw overlay effects (vignette, scanlines)
     */
    drawOverlay() {
        const { width, height } = this.canvas;

        // Subtle vignette
        const vignette = this.ctx.createRadialGradient(
            width / 2, height / 2, height * 0.3,
            width / 2, height / 2, width * 0.8
        );
        vignette.addColorStop(0, 'rgba(0, 0, 0, 0)');
        vignette.addColorStop(1, 'rgba(0, 0, 0, 0.4)');
        this.ctx.fillStyle = vignette;
        this.ctx.fillRect(0, 0, width, height);

        // Subtle scanlines
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
        for (let y = 0; y < height; y += 3) {
            this.ctx.fillRect(0, y, width, 1);
        }
    }

    /**
     * Check if visualizer is currently active
     */
    get active() {
        return this.isActive;
    }
}
